import { useContext, useEffect, useMemo } from "react";
import { CREATE_ACCOUNT_ERROR_DEFAULT_TITLE } from "@shared/features/onboarding/constants";
import { AddClientResponse, OpenMessage } from "@shared/types";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { sharedLib } from "@shared/services/shared.service";

interface useUrlQuickConnectProps {
  queryData: {
    statusCode: string | null;
    failMessage: string | null;
    accountBaseName: string | null;
    exchangesList?: string | null;
  };
  openMessage: OpenMessage;
  onSubmitSuccess?: () => void;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
  getErrorNotification?: (description: string, title?: string) => void;
  closeDrawer: () => void;
  isOnboarding?: boolean;
  isMobileApp?: boolean;
}

const useUrlQuickConnect = ({
  queryData,
  openMessage,
  onSubmitSuccess,
  getSuccessMessage,
  getErrorNotification,
  closeDrawer,
  isOnboarding,
  isMobileApp,
}: useUrlQuickConnectProps) => {
  const {
    createAccountDrawerOpen,
    setSelectedSubExchanges,
    setAccountName,
    getSelectedSubExchangesData,
  } = useContext(AccountFormContext)!;
  const { statusCode, failMessage, accountBaseName, exchangesList } = queryData;

  const { refetch: refetchAccountsList } =
    sharedLib.sharedApi.configuration.useGetClientsListQuery();

  const decodedQueryExchanges = exchangesList
    ? decodeURIComponent(exchangesList)
    : undefined;
  const exchangesArray = useMemo(
    () => decodedQueryExchanges?.replace(/[\[\]']+/g, "").split(", "),
    [decodedQueryExchanges]
  );

  useEffect(() => {
    if (
      statusCode === "200" &&
      (createAccountDrawerOpen || isOnboarding || isMobileApp)
    ) {
      closeDrawer();
      onSubmitSuccess?.();
      refetchAccountsList();
      if (exchangesArray) getSuccessMessage?.(undefined, exchangesArray);
    }
  }, [
    statusCode,
    exchangesArray,
    createAccountDrawerOpen,
    isOnboarding,
    isMobileApp,
  ]);

  useEffect(() => {
    if (statusCode === "400") {
      if (failMessage) {
        getErrorNotification?.(failMessage, CREATE_ACCOUNT_ERROR_DEFAULT_TITLE);
      } else {
        openMessage("Something went wrong, please try again later", "error");
      }
    }
  }, [statusCode, failMessage]);

  useEffect(() => {
    if (statusCode === "400") {
      if (exchangesArray) {
        const subExchanges = getSelectedSubExchangesData(exchangesArray);
        if (subExchanges?.length) setSelectedSubExchanges(subExchanges);
      }
      if (accountBaseName) {
        setAccountName(accountBaseName);
      }
    }
  }, [
    exchangesArray,
    statusCode,
    accountBaseName,
    getSelectedSubExchangesData,
  ]);
};

export default useUrlQuickConnect;
